import { useCallback, useEffect, useState } from "react";
import { Download, Printer, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { DataTable, fmtDate } from "@/components/admin/DataTable";
import { errMsg, useApi } from "@/lib/api";

const Card = ({ label, value, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
    <p className="text-2xl font-extrabold text-slate-900">{value ?? "No data yet"}</p>
    <p className="mt-1 text-sm text-slate-600">{label}</p>
  </div>
);
const pct = (v) => (v === null || v === undefined ? null : `${v}%`);

function Analytics() {
  const api = useApi();
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/analytics", { params: { days } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, [api, days]);
  const csv = async () => {
    const r = await api.get("/admin/analytics", { params: { days, format: "csv" }, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    Object.assign(document.createElement("a"), { href: url, download: `crowlearn-report-${days}d.csv` }).click();
  };
  if (!d) return <p className="text-sm text-slate-600">Loading…</p>;
  const sales = Object.entries(d.sales).map(([c, v]) => `${c} ${v.toLocaleString()}`).join(" · ");
  return (
    <div data-testid="analytics-panel">
      <div className="mb-4 flex flex-wrap items-center gap-2 print:hidden">
        {[7, 30, 90, 365].map((n) => <Button key={n} size="sm" variant={days === n ? "default" : "outline"} onClick={() => setDays(n)} data-testid={`analytics-range-${n}`}>{n} days</Button>)}
        <Button size="sm" variant="outline" onClick={csv} data-testid="analytics-csv"><Download className="mr-1 h-4 w-4" /> CSV</Button>
        <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="analytics-pdf"><Printer className="mr-1 h-4 w-4" /> Print / save as PDF</Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Card label="Registrations" value={d.registrations_total} testid="an-registrations" />
        <Card label="Active users" value={d.active_users} testid="an-active" />
        <Card label="Sales (paid)" value={sales || null} testid="an-sales" />
        <Card label="Checkout conversion" value={pct(d.conversion_pct)} testid="an-conversion" />
        <Card label="Retention (returning users)" value={pct(d.retention_pct)} testid="an-retention" />
        <Card label="Course completion" value={pct(d.completion_pct)} testid="an-completion" />
        <Card label="Assessment pass rate" value={pct(d.assessment_pass_pct)} testid="an-pass" />
        <Card label="Organisation members" value={d.org_members} testid="an-org" />
      </div>
      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="an-popular">
          <h2 className="font-semibold text-slate-900">Course popularity</h2>
          {d.popular_courses.length ? <ul className="mt-3 space-y-2 text-sm">{d.popular_courses.map((c) => <li key={c.title} className="flex justify-between gap-2"><span>{c.title}</span><span className="text-slate-600">{c.enrolments} enrolled · {c.completed} completed</span></li>)}</ul> : <p className="mt-3 text-sm text-slate-600">No data yet</p>}
        </section>
        <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="an-renewals">
          <h2 className="font-semibold text-slate-900">Organisation renewals due (60 days)</h2>
          {d.renewals_due.length ? <ul className="mt-3 space-y-2 text-sm">{d.renewals_due.map((o) => <li key={o.name} className="flex justify-between"><span>{o.name}</span><span>{o.end_date}</span></li>)}</ul> : <p className="mt-3 text-sm text-slate-600">No data yet</p>}
        </section>
      </div>
    </div>
  );
}

function Integrations() {
  const api = useApi();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/integrations").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, [api]);
  if (!d) return <p className="text-sm text-slate-600">Loading…</p>;
  return (
    <div data-testid="integrations-panel">
      <p className="mb-4 text-sm text-slate-700">Payment provider in use: <b data-testid="payments-provider">{d.payments_provider === "none" ? "None selected" : d.payments_provider}</b> (choose it in Settings). Errors in the last 24 hours: <b data-testid="errors-24h">{d.errors_24h}</b>. Keys are entered in the hosting platform's secure secrets panel and are never shown here.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {d.items.map((i) => (
          <div key={i.key} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`integration-${i.key}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="font-semibold text-slate-900">{i.name}</p>
              {i.connected ? <Badge className="gap-1 bg-emerald-600"><CheckCircle2 className="h-3 w-3" /> Connected</Badge> : <Badge variant="outline" className="gap-1"><XCircle className="h-3 w-3" /> Not connected</Badge>}
            </div>
            <p className="mt-1 text-xs uppercase tracking-wide text-slate-500">{i.group}</p>
            <p className="mt-2 text-sm text-slate-700">{i.detail}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function AccessReview() {
  const api = useApi();
  const [d, setD] = useState(null);
  const [notes, setNotes] = useState("");
  const load = useCallback(() => api.get("/admin/access-review").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), [api]);
  useEffect(() => { load(); }, [load]);
  const record = async () => {
    try { await api.post("/admin/access-review", { notes }); toast.success("Review recorded"); setNotes(""); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return <p className="text-sm text-slate-600">Loading…</p>;
  return (
    <div data-testid="access-review-panel">
      <p className="mb-3 text-sm text-slate-700">Last review: {d.last_review ? `${fmtDate(d.last_review.created_at)} by ${d.last_review.reviewer}` : "never"}. Review this list every 90 days and remove access people no longer need.</p>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full min-w-[600px] text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600"><tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Last active</th><th className="p-3">Status</th></tr></thead>
          <tbody>{d.admins.map((a) => <tr key={a.user_id} className="border-t border-slate-100" data-testid="access-review-row"><td className="p-3">{a.name}</td><td className="p-3">{a.email}</td><td className="p-3">{a.role_name}</td><td className="p-3">{fmtDate(a.last_seen_at)}</td><td className="p-3">{a.status}</td></tr>)}</tbody>
        </table>
      </div>
      <Textarea className="mt-4" placeholder="Review notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="access-review-notes" />
      <Button className="mt-2 bg-[#222d99] hover:bg-[#1a237e]" onClick={record} data-testid="access-review-record">Record review</Button>
    </div>
  );
}

function Moderation() {
  const api = useApi();
  const [reload, setReload] = useState(0);
  const act = async (r) => {
    try { await api.post(`/admin/moderation/posts/${r.id}`, { hidden: !r.hidden, reason: r.hidden ? "" : "Hidden by moderator" }); toast.success(r.hidden ? "Post restored" : "Post hidden"); setReload((n) => n + 1); } catch (e) { toast.error(errMsg(e)); }
  };
  const columns = [
    { key: "author_name", label: "Author", render: (r) => r.author_name || r.name || "—" },
    { key: "body", label: "Post", render: (r) => (r.body || "").slice(0, 120) },
    { key: "created_at", label: "Posted", sortable: true, render: (r) => fmtDate(r.created_at) },
    { key: "hidden", label: "Visible", render: (r) => (r.hidden ? "Hidden" : "Visible") },
    { key: "_a", label: "Actions", render: (r) => <Button size="sm" variant="outline" onClick={() => act(r)} data-testid={`moderate-${r.id}`}>{r.hidden ? "Restore" : "Hide"}</Button> },
  ];
  return <DataTable endpoint="/admin/moderation/posts" columns={columns} rowKey="id" testid="moderation-table" reloadKey={reload} />;
}

export const PANELS = {
  analytics: [{ key: "overview", label: "Overview", C: Analytics }],
  operations: [{ key: "status", label: "Status", C: Integrations }],
  privacy: [{ key: "access", label: "Access review", C: AccessReview }],
  support: [{ key: "moderation", label: "Community moderation", C: Moderation }],
  ai: [{ key: "providers", label: "Provider status", C: Integrations }],
};
